import * as React from 'react';

import { cn } from '@/lib/utils';
import { AILogo } from '@/components/logo/ai-logo';

const SQUIRCLE_PATH =
  'M1.1687 5.09221C1.44983 3.03551 3.03409 1.43747 5.09056 1.15464C7.11115 0.876739 9.82098 0.6 12.6 0.6C15.3637 0.6 18.059 0.873703 20.076 1.15005C22.1498 1.43417 23.7418 3.05368 24.0222 5.12794C24.3077 7.23947 24.6 10.0294 24.6 12.6C24.6 15.1875 24.3038 17.9971 24.0166 20.1135C23.7379 22.1667 22.1732 23.7746 20.1207 24.0584C18.1434 24.3319 15.4723 24.6 12.6 24.6C9.71155 24.6 7.02648 24.3288 5.04597 24.0538C3.01073 23.7712 1.45383 22.185 1.17438 20.1493C0.890004 18.0777 0.6 15.3026 0.6 12.6C0.6 9.9154 0.886154 7.15926 1.1687 5.09221Z';

export interface AIBadgeProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
  size?: number;
}

export const AIBadge = React.forwardRef<HTMLDivElement, AIBadgeProps>(
  ({ children, size = 48, className, ...props }, ref) => {
    const fillId = React.useId();
    const strokeId = React.useId();

    return (
      <div ref={ref} className={cn('relative shrink-0', className)} style={{ width: size, height: size }} {...props}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
          viewBox="0 0 25.2 25.2"
          fill="none"
          aria-hidden="true"
        >
          <path
            d={SQUIRCLE_PATH}
            fill={`url(#${fillId})`}
            stroke={`url(#${strokeId})`}
            strokeOpacity="0.7"
            strokeWidth="1.2"
          />
          <defs>
            <linearGradient id={fillId} x1="2.3931" y1="0.6" x2="17.282" y2="22.5523" gradientUnits="userSpaceOnUse">
              <stop offset="0.0544031" stopColor="#1868DB" />
              <stop offset="0.5" stopColor="#357DE8" />
              <stop offset="0.802885" stopColor="#7EE2B8" />
              <stop offset="1" stopColor="#B4EE2B" />
            </linearGradient>
            <linearGradient id={strokeId} x1="2.3931" y1="0.6" x2="17.282" y2="22.5523" gradientUnits="userSpaceOnUse">
              <stop stopColor="#B4EE2B" />
              <stop offset="0.266033" stopColor="#7EE2B8" />
              <stop offset="0.740217" stopColor="#357DE8" />
              <stop offset="0.945597" stopColor="#1868DB" />
            </linearGradient>
          </defs>
        </svg>
        <span
          className="absolute inset-[20%] [&>*]:h-full [&>*]:w-full"
          style={{
            filter: `drop-shadow(0px ${(size * 0.04).toFixed(2)}px ${(size * 0.05).toFixed(2)}px rgba(22,27,53,0.55))`,
          }}
        >
          {children ?? <AILogo variant="light" />}
        </span>
      </div>
    );
  },
);

AIBadge.displayName = 'AIBadge';
